// Alert-rule detail Drawer (docs/09 §4/§7 mandate: alert rules use detail
// drawers, no per-object route). Subject rides `?rule=<id>`; the drawer owns
// its detail query and the rule's recent-events list, while the delete
// mutation stays in the route container (delete success closes the drawer).

import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery, type ListQueryResult } from "../../lib/useListQuery";
import {
  Badge,
  Button,
  Code,
  Drawer,
  EmptyState,
  Inline,
  InlineError,
  KeyValue,
  Section,
  Skeleton,
  Stack,
  Text,
  type KeyValueRow,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { RowConfirm } from "../shared/RowConfirm";
import { AlertRuleAuthoringForm } from "./AlertRuleAuthoringForm";
import { deliverySummary } from "./alertSummaries";
import type { AlertRuleEntityOptions } from "./AlertEntitySelect";
import { useAlertRuleOptions } from "./useAlertRuleOptions";

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertRule = components["schemas"]["AlertRuleResponse"];
type AlertEvent = components["schemas"]["AlertEventResponse"];
type AlertRulePatch = components["schemas"]["AlertRulePatch"];
type AlertRouteWhen = components["schemas"]["AlertRouteWhen"];
type MetricFilters = components["schemas"]["MetricFilters"];
type BatchCompletedScope = components["schemas"]["BatchCompletedScope"];

// Compact in-drawer history: Type · Delivery · Created (the rule is implied).
const ruleEventColumns: Array<ColumnDef<AlertEvent>> = [
  { header: "Type", accessorKey: "event_type" },
  {
    header: "Delivery",
    cell: ({ row }) => deliverySummary(row.original),
  },
  {
    header: "Created",
    cell: ({ row }) => formatDateTime(row.original.created_at),
  },
];

function metricFilterSummary(filters?: MetricFilters): string {
  const parts = Object.entries(filters ?? {})
    .filter((entry): entry is [string, string] => entry[1] != null)
    .map(([key, value]) => `${key.replaceAll("_", " ")} ${value}`);
  return parts.length > 0 ? parts.join(" · ") : "none";
}

function batchScopeSummary(scope?: BatchCompletedScope): string {
  const parts: string[] = [];
  if (scope?.agent_id != null) {
    parts.push(`agent ${scope.agent_id}`);
  }
  if (scope?.template_id != null) {
    parts.push(`template ${scope.template_id}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "all batches";
}

function conditionRows(rule: AlertRule): KeyValueRow[] {
  const trigger = rule.trigger;
  if (trigger.kind === "threshold") {
    return [
      {
        label: "Condition",
        value: `${trigger.metric}.${trigger.measure} ${trigger.comparator} ${String(trigger.threshold)}`,
      },
      {
        label: "Window",
        value: `${String(trigger.window_seconds)}s · min ${String(trigger.min_occurrences)} occurrences`,
      },
      {
        label: "Transitions",
        value: `${String(trigger.consecutive_breaches)} breaches · ${String(trigger.resolve_after_clear)} clears`,
      },
      {
        label: "No data",
        value: `${trigger.no_data_policy} · renotify ${String(trigger.renotify_seconds)}s`,
      },
      { label: "Filters", value: metricFilterSummary(trigger.filters) },
    ];
  }
  return [
    {
      label: "Gate",
      value: trigger.gate
        ? `pass_rate ${trigger.gate.comparator} ${String(trigger.gate.threshold)}`
        : "none",
    },
    { label: "Scope", value: batchScopeSummary(trigger.scope) },
  ];
}

// Channel names resolve from the already-loaded channels cache; an id whose
// channel is not in the cache renders as Code (never hidden).
function channelsValue(
  rule: AlertRule,
  channels: AlertChannel[],
  when: AlertRouteWhen,
) {
  const channelIds =
    rule.routes
      ?.filter((route) => route.when === when)
      .flatMap((route) => route.channel_ids) ?? [];
  if (channelIds.length === 0) {
    return "none";
  }
  return (
    <Inline gap={2}>
      {channelIds.map((channelId) => {
        const channel = channels.find(
          (candidate) => candidate.id === channelId,
        );
        return channel ? (
          <Text as="span" key={channelId}>
            {channel.display_name}
          </Text>
        ) : (
          <Code key={channelId}>{channelId}</Code>
        );
      })}
    </Inline>
  );
}

function ruleDetailRows(
  rule: AlertRule,
  channels: AlertChannel[],
): KeyValueRow[] {
  const trigger = rule.trigger;
  const triggerRows: KeyValueRow[] =
    trigger.kind === "threshold"
      ? [
          {
            label: "Last value",
            value:
              trigger.last_value == null
                ? "none"
                : rule.last_evaluated_at == null
                  ? trigger.last_value.toFixed(4)
                  : `${trigger.last_value.toFixed(4)} · evaluated ${formatDateTime(rule.last_evaluated_at)}`,
          },
          {
            label: "State since",
            value: formatDateTime(trigger.state_since),
          },
        ]
      : [
          {
            label: "Batch watermark",
            value: formatDateTime(trigger.batch_watermark),
          },
        ];
  return [
    { label: "Trigger", value: trigger.kind },
    { label: "Severity", value: rule.severity },
    { label: "Enabled", value: rule.enabled ? "true" : "false" },
    ...conditionRows(rule),
    {
      label: "Firing channels",
      value: channelsValue(rule, channels, "firing"),
    },
    {
      label: "Resolved channels",
      value: channelsValue(rule, channels, "resolved"),
    },
    ...triggerRows,
    {
      label: "Silenced until",
      value: formatDateTime(rule.silence_expires_at),
    },
    { label: "Next eval", value: formatDateTime(rule.next_eval_at) },
    {
      label: "Created",
      value:
        rule.created_by == null
          ? formatDateTime(rule.created_at)
          : `${formatDateTime(rule.created_at)} by ${rule.created_by}`,
    },
    { label: "Updated", value: formatDateTime(rule.updated_at) },
  ];
}

export interface AlertRuleDrawerViewProps {
  canEdit: boolean;
  channelOptionsErrorMessage?: string;
  channels: AlertChannel[];
  entityOptions?: AlertRuleEntityOptions;
  editErrorMessage?: string;
  errorMessage?: string;
  events: ListQueryResult<AlertEvent>;
  isDeletePending: boolean;
  isChannelOptionsPending?: boolean;
  isEditPending: boolean;
  isPending: boolean;
  onDelete: () => void;
  onDismiss: () => void;
  onEdit: (body: AlertRulePatch) => Promise<void>;
  onEditReset: () => void;
  onRetryChannelOptions?: () => void;
  onSilence: () => void;
  rule?: AlertRule;
}

export function AlertRuleDrawerView({
  canEdit,
  channelOptionsErrorMessage,
  channels,
  entityOptions,
  editErrorMessage,
  errorMessage,
  events,
  isDeletePending,
  isChannelOptionsPending,
  isEditPending,
  isPending,
  onDelete,
  onDismiss,
  onEdit,
  onEditReset,
  onRetryChannelOptions,
  onSilence,
  rule,
}: AlertRuleDrawerViewProps) {
  // Reveal-only state for the §2.12 confirm; failure keeps it open (the
  // container only closes the drawer on delete success).
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const editRequestRef = useRef<Promise<void> | null>(null);
  const isEditLocked = isEditPending || isEditSubmitting;

  useEffect(() => {
    setIsEditing(false);
  }, [rule?.id]);

  return (
    <Drawer
      ariaLabel="Alert rule detail"
      isOpen
      onDismiss={() => {
        if (!isEditLocked) {
          onDismiss();
        }
      }}
      size="lg"
    >
      {isPending ? (
        <Skeleton label="Loading alert rule" rows={6} shape="detail-field" />
      ) : null}
      {errorMessage != null ? <InlineError>{errorMessage}</InlineError> : null}
      {rule && isEditing ? (
        <AlertRuleAuthoringForm
          actionsPlacement="footer"
          channels={channels}
          entityOptions={entityOptions}
          errorMessage={editErrorMessage}
          isOptionsPending={isChannelOptionsPending ?? false}
          isPending={isEditLocked}
          isPreviewPending={false}
          onCancel={() => {
            if (isEditLocked) {
              return;
            }
            onEditReset();
            setIsEditing(false);
          }}
          onRetryOptions={onRetryChannelOptions}
          onSubmit={async (body) => {
            if (editRequestRef.current != null) {
              return;
            }
            setIsEditSubmitting(true);
            const request = onEdit({
              ...body,
              message_config: null,
            });
            editRequestRef.current = request;
            void request
              .then(
                () => {
                  setIsEditing(false);
                },
                () => undefined,
              )
              .finally(() => {
                if (editRequestRef.current === request) {
                  editRequestRef.current = null;
                  setIsEditSubmitting(false);
                }
              });
            await request;
          }}
          rule={rule}
          submitLabel="Save rule"
          optionsErrorMessage={channelOptionsErrorMessage}
        />
      ) : rule ? (
        <Stack gap={5}>
          <Inline gap={3}>
            <Text>{rule.display_name}</Text>
            <Badge
              tone={
                rule.trigger.kind === "threshold" &&
                rule.trigger.state === "firing"
                  ? "danger"
                  : rule.trigger.kind === "threshold"
                    ? "success"
                    : "neutral"
              }
            >
              {rule.trigger.kind === "threshold" ? rule.trigger.state : "event"}
            </Badge>
          </Inline>
          <KeyValue rows={ruleDetailRows(rule, channels)} />
          <Section heading="Recent events">
            <PaginatedTableSection
              ariaLabel="Recent alert events"
              columns={ruleEventColumns}
              emptyState={<EmptyState title="No alert events" />}
              errorMessage={events.errorMessage}
              getRowId={(event) => event.id}
              hasNextPage={events.hasNextPage}
              isFetchingNextPage={events.isFetchingNextPage}
              isPending={events.isPending}
              items={events.items}
              listStateKey={events.listStateKey}
              onLoadMore={events.fetchNextPage}
              skeletonLabel="Loading alert events"
              skeletonRows={3}
            />
          </Section>
          {canEdit ? (
            <>
              <Inline>
                <Button
                  disabled={isEditLocked}
                  type="button"
                  onClick={() => {
                    onEditReset();
                    setIsEditing(true);
                  }}
                >
                  Edit rule
                </Button>
                <Button
                  disabled={isEditLocked}
                  onClick={onSilence}
                  type="button"
                  variant="secondary"
                >
                  Silence rule
                </Button>
              </Inline>
              <RowConfirm
                confirmLabel="Confirm delete"
                isConfirming={isConfirmingDelete}
                isPending={isDeletePending}
                onCancel={() => {
                  setIsConfirmingDelete(false);
                }}
                onConfirm={onDelete}
                onStart={() => {
                  setIsConfirmingDelete(true);
                }}
                pendingLabel="Deleting..."
                sentence={`Delete alert rule ${rule.display_name}? This removes the rule and stops future alert events from it.`}
                triggerLabel="Delete rule"
              />
            </>
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}

export interface AlertRuleDrawerProps {
  canEdit: boolean;
  channelOptionsErrorMessage?: string;
  channels: AlertChannel[];
  editErrorMessage?: string;
  isDeletePending: boolean;
  isChannelOptionsPending?: boolean;
  isEditPending: boolean;
  onDelete: () => void;
  onDismiss: () => void;
  onEdit: (body: AlertRulePatch) => Promise<void>;
  onEditReset: () => void;
  onRetryChannelOptions?: () => void;
  onSilence: () => void;
  ruleId: string;
}

export function AlertRuleDrawer({
  canEdit,
  channelOptionsErrorMessage,
  channels,
  editErrorMessage,
  isDeletePending,
  isChannelOptionsPending,
  isEditPending,
  onDelete,
  onDismiss,
  onEdit,
  onEditReset,
  onRetryChannelOptions,
  onSilence,
  ruleId,
}: AlertRuleDrawerProps) {
  const ruleOptions = useAlertRuleOptions();
  // A 404 on a load-time deep link renders the drawer error state and keeps
  // the param until dismiss; post-delete refetch never happens because the
  // container closes the drawer (navigates `rule` away) on delete success.
  const rule = useQuery({
    queryKey: queryKeys.alertRules.detail(ruleId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/alert-rules/{id}", {
        params: { path: { id: ruleId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const events = useListQuery("/v1/alert-events", {
    params: { alert_rule_id: ruleId },
    queryKey: queryKeys.alertEvents.list({ alert_rule_id: ruleId }),
  });

  return (
    <AlertRuleDrawerView
      canEdit={canEdit}
      channelOptionsErrorMessage={
        channelOptionsErrorMessage ?? ruleOptions.errorMessage
      }
      channels={channels}
      entityOptions={{
        agents: ruleOptions.agents,
        agentVersions: ruleOptions.agentVersions,
        batches: ruleOptions.batches,
        markers: ruleOptions.markers,
        templates: ruleOptions.templates,
      }}
      editErrorMessage={editErrorMessage}
      errorMessage={rule.isError ? rule.error.message : undefined}
      events={events}
      isDeletePending={isDeletePending}
      isChannelOptionsPending={isChannelOptionsPending || ruleOptions.isPending}
      isEditPending={isEditPending}
      isPending={rule.isPending}
      onDelete={onDelete}
      onDismiss={onDismiss}
      onEdit={onEdit}
      onEditReset={onEditReset}
      onRetryChannelOptions={() => {
        onRetryChannelOptions?.();
        ruleOptions.retry();
      }}
      onSilence={onSilence}
      rule={rule.data}
    />
  );
}
